import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { hotEvents } from "@/mocks/events";
import { SectionHeading } from "@/components/base/Shared";
import { formatHeat } from "@/utils/format";

export default function SpreadAnalysis() {
  const event = hotEvents[0];

  return (
    <section id="spread" className="bg-background-50 py-16 md:py-24">
      <div className="w-full px-4 md:px-8">
        <SectionHeading
          eyebrow="SPREAD CURVE"
          title="传播路径：热度曲线比结论更诚实"
          desc="下图是当前榜首事件的相对热度序列，以自身峰值为 100 折算，因此可以和其他事件对比曲线形状，看清陡升段与长尾段分别出现在什么时候。"
        />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          <div className="lg:col-span-8 rounded-2xl bg-background-100 border border-background-200 p-5 md:p-7">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <p className="text-xs font-label text-foreground-500">{event.category} · {event.plat}首爆</p>
                <h3 className="mt-1.5 font-heading text-lg md:text-xl font-bold text-foreground-950">
                  {event.title}
                </h3>
              </div>
              <div className="flex items-center gap-4">
                <div>
                  <p className="text-xs text-foreground-500">当前热度</p>
                  <p className="font-label text-base text-primary-600">{formatHeat(event.heat)}</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">峰值时间</p>
                  <p className="font-label text-base text-foreground-900">
                    {event.peakTime.split(" ")[1]}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 h-[240px] md:h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={event.heatSeries} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                  <defs>
                    <linearGradient id="heatFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="oklch(var(--primary-500))" stopOpacity={0.45} />
                      <stop offset="100%" stopColor="oklch(var(--primary-500))" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="oklch(var(--background-300))" vertical={false} />
                  <XAxis
                    dataKey="time"
                    tick={{ fontSize: 11, fill: "oklch(var(--foreground-500))" }}
                    axisLine={{ stroke: "oklch(var(--background-300))" }}
                    tickLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "oklch(var(--foreground-500))" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "oklch(var(--background-50))",
                      border: "1px solid oklch(var(--background-300))",
                      borderRadius: 8,
                      fontSize: 12,
                      color: "oklch(var(--foreground-900))",
                    }}
                    labelStyle={{ color: "oklch(var(--foreground-600))" }}
                    formatter={(value: any) => [`相对热度 ${value}`, ""]}
                  />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="oklch(var(--primary-500))"
                    strokeWidth={2.5}
                    fill="url(#heatFill)"
                    dot={{ r: 3, fill: "oklch(var(--primary-500))", strokeWidth: 0 }}
                    activeDot={{ r: 5 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-sm bg-primary-500" />
                相对热度（峰值为 100）
              </span>
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-sm bg-accent-400" />
                观测窗口 {event.duration}
              </span>
            </div>
          </div>

          <div className="lg:col-span-4 flex flex-col gap-6">
            <div className="rounded-2xl bg-background-50 border border-background-200 p-5 md:p-6">
              <h3 className="font-heading text-base font-bold text-foreground-950 flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center text-secondary-600">
                  <i className="ri-node-tree" />
                </span>
                扩散渠道占比
              </h3>
              <ul className="mt-5 space-y-4">
                {event.channels.map((channel) => (
                  <li key={channel.name}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-foreground-700">{channel.name}</span>
                      <span className="font-label text-foreground-900">{channel.value}%</span>
                    </div>
                    <div className="mt-2 h-2 rounded-full bg-background-200 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-secondary-400 to-secondary-600"
                        style={{ width: `${channel.value}%` }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl bg-accent-50 border border-accent-200 p-5 md:p-6 flex-1">
              <h3 className="font-heading text-base font-bold text-accent-900 flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-lightbulb-flash-line" />
                </span>
                追踪提示
              </h3>
              <p className="mt-3 text-sm text-accent-900/80 leading-relaxed">
                该事件的陡升段出现在首爆后第 13 小时，由媒体求证与双方声明共同推动。
                当热度曲线在高位停留超过 12 小时，通常意味着「回应本身成了新的传播素材」，
                这类事件的长尾会更长。
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}